import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../API';

/**
 * Treat HTTP 200 + success:false as a mutation failure (matches approveConnection).
 */
function assertNetworkSuccess(response, fallback) {
    if (response.status !== 200 || (response.data && response.data.success === false)) {
        const error = new Error(response.data?.error || fallback);
        error.response = { data: response.data };
        throw error;
    }
    return response;
}

/**
 * Custom hook that consolidates all network-related mutations
 */
export function useNetworkMutations() {
    const queryClient = useQueryClient();

    const refreshConnections = useMutation({
        mutationFn: () => true,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['local-connections'] }),
    });

    const deleteConnection = useMutation({
        mutationFn: async (id) => {
            const response = await api.delete('network/connection', { query: { id } });
            return assertNetworkSuccess(response, 'Could not delete connection');
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['local-connections'] }),
    });

    const cancelConnection = useMutation({
        mutationFn: async (id) => {
            const response = await api.put('network/connection', {
                query: { id, action: 'cancel' },
            });
            return assertNetworkSuccess(response, 'Could not cancel connection');
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['local-connections'] }),
    });

    const rejectConnection = useMutation({
        mutationFn: async (id) => {
            const response = await api.put('network/connection', {
                query: { id, action: 'reject' },
            });
            return assertNetworkSuccess(response, 'Could not reject connection');
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['local-connections'] }),
    });

    const approveConnection = useMutation({
        mutationFn: async ({ id, assetstoreAction, existingAssetstoreId }) => {
            const query = { id, action: 'approve' };
            if (assetstoreAction) {
                query.assetstoreAction = assetstoreAction;
                if (existingAssetstoreId) {
                    query.existingAssetstoreId = existingAssetstoreId;
                }
            }
            const response = await api.put('network/connection', { query });
            return assertNetworkSuccess(response, 'Failed to approve connection');
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['local-connections'] }),
        onError: (error) => {
            console.error('Error approving connection:', error);
        },
    });

    const terminateConnection = useMutation({
        mutationFn: async (connectionId) => {
            const response = await api.put('network/connection', {
                query: { id: connectionId, action: 'terminate' },
            });
            // New shape: { success, connection, remoteNotify }; old shape: connection doc
            if (response.status !== 200) {
                const error = new Error(
                    response.data?.error || 'Could not terminate connection'
                );
                error.response = { data: response.data };
                throw error;
            }
            if (response.data && response.data.success === false) {
                const error = new Error(
                    response.data?.error || 'Could not terminate connection'
                );
                error.response = { data: response.data };
                throw error;
            }
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['local-connections'] });
        },
        onError: (error) => {
            console.error('Error terminating connection:', error);
        },
    });

    const toggleRemote = useMutation({
        mutationFn: ({ id, disableApiKey }) =>
            api.put('network/activate', { query: { id, disableApiKey } }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['local-connections'] }),
    });

    const syncRemote = useMutation({
        mutationFn: ({ connectionId, selectedUuids }) => {
            console.log('[Frontend Sync] Calling sync API', { connectionId, selectedUuids });
            const query = { connectionId };
            const body = selectedUuids?.length
                ? { selectedUuids: JSON.stringify(selectedUuids) }
                : undefined;
            return api.put('network/sync', { query, body });
        },
        onSuccess: (data, variables) => {
            console.log('[Frontend Sync] Sync API call successful', { data, variables });

            if (data?.data?.statistics) {
                const stats = data.data.statistics;
                console.log('[Frontend Sync] Sync statistics:', {
                    newNodes: stats.newNodes,
                    nodesToRemove: stats.nodesToRemove,
                    newFolders: stats.newFolders,
                    newItems: stats.newItems,
                    itemsToRemove: stats.itemsToRemove,
                    foldersToUpdate: stats.foldersToUpdate,
                    itemsToUpdate: stats.itemsToUpdate,
                });
            }

            queryClient.invalidateQueries({ queryKey: ['local-connections'] });
            if (variables.connectionId) {
                queryClient.invalidateQueries({
                    queryKey: ['sync-record', variables.connectionId],
                });
                queryClient.invalidateQueries({
                    queryKey: ['sync-status', variables.connectionId],
                });
            }
        },
        onError: (error, variables) => {
            console.error('[Frontend Sync] Sync API call failed', { error, variables });
        },
    });

    const fetchSyncDiff = useMutation({
        mutationFn: async ({ connectionId }) => {
            const res = await api.get('network/sync/diff', { query: { connectionId } });
            if (res.status !== 200) {
                const msg =
                    res.data?.message || res.data?.error || 'Failed to fetch sync diff';
                throw new Error(msg);
            }
            return res;
        },
    });

    const updateAutoPull = useMutation({
        mutationFn: async ({ connectionId, autoPull }) => {
            const response = await api.put('network/connection/autoPull', {
                query: { id: connectionId },
                body: { autoPull },
            });
            return assertNetworkSuccess(response, 'Could not update auto-sync settings');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['local-connections'] });
        },
    });

    const switchConnectionType = useMutation({
        mutationFn: async ({ girderNetworkUuid, connectionType }) => {
            const response = await api.put('network/connection/type', {
                query: {
                    girderNetworkUuid,
                    connectionType,
                },
            });
            return assertNetworkSuccess(response, 'Could not change connection type');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['local-connections'] });
        },
        onError: (error) => {
            console.error('Error switching connection type:', error);
        },
    });

    return {
        refreshConnections,
        deleteConnection,
        cancelConnection,
        rejectConnection,
        approveConnection,
        terminateConnection,
        toggleRemote,
        syncRemote,
        fetchSyncDiff,
        updateAutoPull,
        switchConnectionType,
    };
}
